/**
 * Whether a string from analysis output may be used as a link target.
 *
 * The report renders text nobody here wrote: YARA rule metadata an operator
 * loaded from a third party, and model output. Both reach an `href`, and an
 * `href` is the one attribute where a string becomes executable, because
 * `javascript:` and `data:` are navigable schemes.
 */

/**
 * Only http and https reach an href. Everything else renders as plain text.
 *
 * The slashes are bracketed rather than escaped so the pattern carries no `//`
 * sequence of its own, which reads badly next to a comment.
 */
const SAFE_PROTOCOL = /^https?:[/][/]/i;

/**
 * Reports whether url is safe to place in an href.
 *
 * Trimmed first, because a reference copied out of a rule file often carries a
 * trailing newline and a link is still a link. Only trimmed, though: the check
 * is a positive allowlist anchored at the start, so a scheme mangled by
 * whitespace fails it either way, and normalising further would buy tolerance
 * rather than safety.
 *
 * The caller renders the original string. This trims to decide, never to
 * display.
 */
export function isSafeHref(url: string): boolean {
  return SAFE_PROTOCOL.test(url.trim());
}

/**
 * Reports whether url is a path INSIDE this app, safe to hand to the router.
 *
 * A different question from `isSafeHref`, and the two do not substitute for
 * each other. That one asks whether a string may become a link at all, and
 * admits the whole of http(s) because a report cites the internet. This one
 * asks whether a string names a route here, and admits nothing else.
 *
 * It exists because react-router's `Link` treats any absolute `to` as external
 * and writes it into the anchor verbatim, SPA handler removed - so a wire
 * string that reached `to` unchecked would be a one-click navigation to
 * wherever the producer said, and `javascript:` is a producer could say. The
 * strings that reach it are not ours: the notifier's `link`, and a citation
 * href the assistant's model wrote from a context holding attacker-authored
 * telemetry.
 *
 * Protocol-relative (`//host`) and the backslash form browsers normalise to it
 * are rejected explicitly - both begin with a slash and both leave the origin.
 */
export function isInAppPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
}

/** Whether a rendered link is navigable, or shown as the text it is. */
export type LinkMode = 'live' | 'inert';

/**
 * Whether url may be placed in an href under mode.
 *
 * Two independent reasons a link does not become an anchor, and neither
 * substitutes for the other. The scheme allowlist above is a safety rule and
 * applies always. `inert` is the ANALYST's choice: defanging a report says
 * they do not want its text clickable, and the narrative quoting the page's
 * own URL is exactly the text they meant — a live anchor there is one stray
 * click onto the kit the report is about.
 *
 * This is the one place that decides it. A second rule elsewhere is what the
 * frontend CLAUDE.md forbids.
 */
export function isNavigable(url: string, mode: LinkMode): boolean {
  return mode === 'live' && isSafeHref(url);
}
